import dayjs from 'dayjs'
import PracticePlanEnum from '@/enums/PracticePlanEnum'
import BillingEngineEnum from '@/enums/BillingEngineEnum'
import BillingPeriodEnum from '@/enums/BillingPeriodEnum'
import PracticeTypeEnum from '@/enums/PracticeTypeEnum'

const PAID_PLAN_CODES = Object.freeze([
    PracticePlanEnum.STARTER,
    PracticePlanEnum.PROFESSIONAL,
    PracticePlanEnum.PRACTICE_PLUS,
    PracticePlanEnum.CUSTOM,
    PracticePlanEnum.ANCILLARY_PREMIUM,
    PracticePlanEnum.PREMIUM,
    PracticePlanEnum.ENTERPRISE,
])

const FREE_PLAN_CODES = Object.freeze([PracticePlanEnum.FREE, PracticePlanEnum.NETWORK])

const PLAN_DISPLAY_NAMES = Object.freeze({
    [PracticePlanEnum.FREE]: 'Free',
    [PracticePlanEnum.STARTER]: 'Starter',
    [PracticePlanEnum.PROFESSIONAL]: 'Professional',
    [PracticePlanEnum.PRACTICE_PLUS]: 'Practice Plus',
    [PracticePlanEnum.CUSTOM]: 'Custom',
    [PracticePlanEnum.ANCILLARY_PREMIUM]: 'Premium',
    [PracticePlanEnum.NETWORK]: 'Network',
    [PracticePlanEnum.PREMIUM]: 'Premium',
    [PracticePlanEnum.ENTERPRISE]: 'Enterprise',
    [PracticePlanEnum.TRIAL_PERIOD]: 'Trial',
    [PracticePlanEnum.PENDING_TRIAL]: 'Pending Trial',
})

const ANNUAL_MONTHS_CHARGED = 8
const EXTRA_REFERRAL_PRICE = 1.99
const EXTRA_SEAT_MONTHLY_PRICE = 59
const SALES_EMAIL = 'success@medmatchnetwork.com'

function toAnnualPrice(monthlyPrice) {
    if (monthlyPrice === null || monthlyPrice === undefined) {
        return null
    }
    return Number((monthlyPrice * ANNUAL_MONTHS_CHARGED).toFixed(2))
}

/** Signup / unauthenticated fallback matching backend enrichPlan shape */
const PHYSICIAN_FALLBACK_PLANS = Object.freeze([
    Object.freeze({
        code: PracticePlanEnum.FREE,
        name: 'Free',
        monthlyPrice: 0,
        annualPrice: 0,
        includedSeats: null,
        includedReferralsPerMonth: null,
        canProposeSlots: false,
        canReceiveReferrals: false,
        hasMeteredReferrals: false,
        hasExtraSeats: false,
        hasTieredLocations: false,
        extraSeatMonthlyPrice: null,
        extraSeatAnnualPrice: null,
        extraReferralPrice: null,
        practiceTypeGroup: null,
    }),
    Object.freeze({
        code: PracticePlanEnum.STARTER,
        name: 'Starter',
        monthlyPrice: 149,
        annualPrice: toAnnualPrice(149),
        includedSeats: 5,
        includedReferralsPerMonth: 100,
        canProposeSlots: true,
        canReceiveReferrals: true,
        hasMeteredReferrals: true,
        hasExtraSeats: true,
        hasTieredLocations: false,
        extraSeatMonthlyPrice: EXTRA_SEAT_MONTHLY_PRICE,
        extraSeatAnnualPrice: toAnnualPrice(EXTRA_SEAT_MONTHLY_PRICE),
        extraReferralPrice: EXTRA_REFERRAL_PRICE,
        practiceTypeGroup: 'PHYSICIAN',
    }),
    Object.freeze({
        code: PracticePlanEnum.PROFESSIONAL,
        name: 'Professional',
        monthlyPrice: 399,
        annualPrice: toAnnualPrice(399),
        includedSeats: null,
        includedReferralsPerMonth: 500,
        canProposeSlots: true,
        canReceiveReferrals: true,
        hasMeteredReferrals: true,
        hasExtraSeats: false,
        hasTieredLocations: false,
        extraSeatMonthlyPrice: null,
        extraSeatAnnualPrice: null,
        extraReferralPrice: EXTRA_REFERRAL_PRICE,
        practiceTypeGroup: 'PHYSICIAN',
    }),
    Object.freeze({
        code: PracticePlanEnum.PRACTICE_PLUS,
        name: 'Practice Plus',
        monthlyPrice: 799,
        annualPrice: toAnnualPrice(799),
        includedSeats: null,
        includedReferralsPerMonth: 1500,
        canProposeSlots: true,
        canReceiveReferrals: true,
        hasMeteredReferrals: true,
        hasExtraSeats: false,
        hasTieredLocations: false,
        extraSeatMonthlyPrice: null,
        extraSeatAnnualPrice: null,
        extraReferralPrice: EXTRA_REFERRAL_PRICE,
        practiceTypeGroup: 'PHYSICIAN',
    }),
    Object.freeze({
        code: PracticePlanEnum.CUSTOM,
        name: 'Custom',
        monthlyPrice: null,
        annualPrice: null,
        includedSeats: null,
        includedReferralsPerMonth: null,
        canProposeSlots: true,
        canReceiveReferrals: true,
        hasMeteredReferrals: false,
        hasExtraSeats: false,
        hasTieredLocations: false,
        extraSeatMonthlyPrice: null,
        extraSeatAnnualPrice: null,
        extraReferralPrice: null,
        practiceTypeGroup: 'PHYSICIAN',
    }),
])

const ANCILLARY_FALLBACK_PLANS = Object.freeze([
    Object.freeze({
        code: PracticePlanEnum.FREE,
        name: 'Free',
        monthlyPrice: 0,
        annualPrice: 0,
        includedSeats: null,
        includedReferralsPerMonth: null,
        canProposeSlots: false,
        canReceiveReferrals: false,
        hasMeteredReferrals: false,
        hasExtraSeats: false,
        hasTieredLocations: false,
        extraSeatMonthlyPrice: null,
        extraSeatAnnualPrice: null,
        extraReferralPrice: null,
        practiceTypeGroup: null,
    }),
    Object.freeze({
        code: PracticePlanEnum.ANCILLARY_PREMIUM,
        name: 'Premium',
        monthlyPrice: null,
        annualPrice: null,
        includedSeats: null,
        includedReferralsPerMonth: 0,
        canProposeSlots: true,
        canReceiveReferrals: true,
        hasMeteredReferrals: true,
        hasExtraSeats: false,
        hasTieredLocations: true,
        extraSeatMonthlyPrice: null,
        extraSeatAnnualPrice: null,
        extraReferralPrice: null,
        practiceTypeGroup: 'ANCILLARY',
        annualMonthsCharged: ANNUAL_MONTHS_CHARGED,
        locationTiers: [
            { upTo: 3, monthlyPrice: 199 },
            { upTo: 9, monthlyPrice: 169 },
            { upTo: null, monthlyPrice: 149 },
        ],
        referralTiers: [
            { upTo: 50, price: 9.99 },
            { upTo: 200, price: 8.99 },
            { upTo: null, price: 7.99 },
        ],
        locationAnnualTiers: [
            { upTo: 3, annualPrice: toAnnualPrice(199) },
            { upTo: 9, annualPrice: toAnnualPrice(169) },
            { upTo: null, annualPrice: toAnnualPrice(149) },
        ],
    }),
])

function isPlanEngine(practice) {
    return practice?.billingEngine === BillingEngineEnum.PLAN
}

function isPaidPlanCode(code) {
    return PAID_PLAN_CODES.includes(code)
}

function isFreePlanCode(code) {
    return FREE_PLAN_CODES.includes(code)
}

function isCustomPlan(code) {
    return code === PracticePlanEnum.CUSTOM
}

function intervalToApi(isAnnual) {
    return isAnnual ? BillingPeriodEnum.ANNUAL : BillingPeriodEnum.MONTHLY
}

function getEffectivePlanCode(practice) {
    if (!practice) {
        return PracticePlanEnum.FREE
    }
    if (practice.planCode) {
        return practice.planCode
    }
    if (practice.subscription === PracticePlanEnum.TRIAL_PERIOD) {
        return PracticePlanEnum.FREE
    }
    if (practice.subscription === PracticePlanEnum.NETWORK) {
        return PracticePlanEnum.FREE
    }
    if (practice.subscription === PracticePlanEnum.PREMIUM) {
        return PracticePlanEnum.STARTER
    }
    if (practice.subscription === PracticePlanEnum.ENTERPRISE) {
        return PracticePlanEnum.ANCILLARY_PREMIUM
    }
    return practice.subscription || PracticePlanEnum.FREE
}

function hasActiveTrial(practice) {
    if (!practice?.trialPeriodEnds) {
        return false
    }
    return dayjs(practice.trialPeriodEnds).isAfter(dayjs())
}

function isPracticeOnPaidPlan(practice) {
    if (!practice) {
        return false
    }
    if (isPlanEngine(practice) && practice.planCode) {
        return isPaidPlanCode(practice.planCode)
    }
    return [PracticePlanEnum.PREMIUM, PracticePlanEnum.ENTERPRISE].includes(practice.subscription)
}

function isPracticeOnPendingTrial(practice) {
    if (!practice) {
        return false
    }
    return practice.subscription === PracticePlanEnum.PENDING_TRIAL
}

function isPracticeOnTrial(practice) {
    if (!practice) {
        return false
    }
    if (practice.subscription === PracticePlanEnum.TRIAL_PERIOD) {
        return true
    }
    if (isPlanEngine(practice) && isFreePlanCode(practice.planCode || PracticePlanEnum.FREE)) {
        return hasActiveTrial(practice)
    }
    return false
}

function isPracticeOnFreePlan(practice) {
    if (!practice) {
        return true
    }
    if (isPracticeOnPaidPlan(practice) || isPracticeOnTrial(practice)) {
        return false
    }
    const code = getEffectivePlanCode(practice)
    return isFreePlanCode(code) || practice.subscription === PracticePlanEnum.NETWORK
}

function getPlanDisplayName(code) {
    return PLAN_DISPLAY_NAMES[code] || code || 'Free'
}

function formatLimit(value, unitLabel) {
    if (value === null || value === undefined) {
        return `Unlimited ${unitLabel}`
    }
    return `${value.toLocaleString()} ${unitLabel}`
}

function formatPlanLimitsSummary(plan) {
    if (!plan) {
        return ''
    }
    if (plan.code === PracticePlanEnum.FREE) {
        return 'Forever, for outbound referrals'
    }
    if (plan.code === PracticePlanEnum.CUSTOM) {
        return 'Multi-site, SSO, Network Intelligence'
    }
    if (plan.code === PracticePlanEnum.STARTER) {
        return `Up to ${plan.includedSeats} members · ${plan.includedReferralsPerMonth} inbound referrals/mo`
    }
    if (plan.code === PracticePlanEnum.PROFESSIONAL) {
        return `Up to ${plan.includedReferralsPerMonth.toLocaleString()} referrals/mo · full SLA tracking`
    }
    if (plan.code === PracticePlanEnum.PRACTICE_PLUS) {
        return `Up to ${plan.includedReferralsPerMonth.toLocaleString()} referrals/mo · API access`
    }
    if (plan.hasTieredLocations) {
        const firstTier = plan.locationTiers?.[0]
        const fromPrice = firstTier?.monthlyPrice
        return fromPrice != null
            ? `From $${fromPrice}/mo per location · confirmed referral fees`
            : 'Per-location pricing · confirmed referral fees'
    }
    const seats =
        plan.includedSeats === null || plan.includedSeats === undefined
            ? 'Unlimited seats'
            : `Up to ${plan.includedSeats} members`
    const referrals =
        plan.includedReferralsPerMonth === null || plan.includedReferralsPerMonth === undefined
            ? 'unlimited referrals/mo'
            : `${plan.includedReferralsPerMonth.toLocaleString()} inbound referrals/mo`
    return `${seats} · ${referrals}`
}

function formatPriceDisplay(plan, { isAnnual = false } = {}) {
    if (!plan) {
        return ''
    }
    if (plan.code === PracticePlanEnum.CUSTOM) {
        return 'Custom'
    }
    if (plan.hasTieredLocations) {
        const firstTier = plan.locationTiers?.[0]
        if (firstTier?.monthlyPrice != null) {
            return `From $${firstTier.monthlyPrice}`
        }
        return 'Custom'
    }
    if (plan.monthlyPrice === 0) {
        return 'Free'
    }
    if (plan.monthlyPrice == null) {
        return 'Custom'
    }
    if (isAnnual && plan.annualPrice != null) {
        return `$${plan.annualPrice}`
    }
    return `$${plan.monthlyPrice}`
}

function getCtaLabel(plan, { isCurrent = false, isPaid = false } = {}) {
    if (!plan) {
        return ''
    }
    if (plan.code === PracticePlanEnum.CUSTOM) {
        return 'Contact Sales'
    }
    if (isCurrent) {
        return 'Current Plan'
    }
    if (plan.code === PracticePlanEnum.FREE) {
        return 'Join Free'
    }
    if (isPaid) {
        return `Change to ${plan.name}`
    }
    if (plan.code === PracticePlanEnum.STARTER) {
        return 'Start With Starter'
    }
    return `Choose ${plan.name}`
}

function isMostPopular(plan) {
    return plan?.code === PracticePlanEnum.PROFESSIONAL
}

function getFallbackPlans(practiceType) {
    if (practiceType === PracticeTypeEnum.ANCILLARY || practiceType === 'demo-ancillary' || practiceType === 'other') {
        return ANCILLARY_FALLBACK_PLANS.map((p) => ({ ...p }))
    }
    return PHYSICIAN_FALLBACK_PLANS.map((p) => ({ ...p }))
}

function mapLegacySubscriptionToPlanCode(subscription, practiceType) {
    if (subscription === PracticePlanEnum.TRIAL_PERIOD || subscription === PracticePlanEnum.NETWORK) {
        return PracticePlanEnum.FREE
    }
    if (subscription === PracticePlanEnum.PREMIUM) {
        return PracticePlanEnum.STARTER
    }
    if (subscription === PracticePlanEnum.ENTERPRISE) {
        return practiceType === PracticeTypeEnum.ANCILLARY ? PracticePlanEnum.ANCILLARY_PREMIUM : PracticePlanEnum.CUSTOM
    }
    return subscription
}

function shouldUseSubscribe(practice) {
    if (!practice) {
        return true
    }
    return !isPlanEngine(practice) || !practice.planCode || practice.planCode === PracticePlanEnum.FREE
}

function getSalesMailto() {
    return `mailto:${SALES_EMAIL}`
}

export {
    PAID_PLAN_CODES,
    FREE_PLAN_CODES,
    EXTRA_REFERRAL_PRICE,
    EXTRA_SEAT_MONTHLY_PRICE,
    SALES_EMAIL,
    isPlanEngine,
    isPaidPlanCode,
    isFreePlanCode,
    isCustomPlan,
    intervalToApi,
    getEffectivePlanCode,
    hasActiveTrial,
    isPracticeOnPaidPlan,
    isPracticeOnTrial,
    isPracticeOnFreePlan,
    isPracticeOnPendingTrial,
    getPlanDisplayName,
    formatLimit,
    formatPlanLimitsSummary,
    formatPriceDisplay,
    getCtaLabel,
    isMostPopular,
    getFallbackPlans,
    mapLegacySubscriptionToPlanCode,
    shouldUseSubscribe,
    getSalesMailto,
}
